import { reflect } from '@effector/reflect';
import { Stack } from '@mantine/core';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RTP_MEDIAN_VALUE } from 'features/edit-game-setting/config';
import { viewerModel } from 'entities/viewer';
import { createDynamicForm, createModalForm, ModalFormProps } from 'shared/ui';
import { EditGameSettingModel } from '../../model';

export const createEditGameSettingModal = (model: EditGameSettingModel) => {
	const ModalForm = createModalForm(model);

	const DynamicForm = createDynamicForm(model.dynamicFormModel);

	const EditGameSettingModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [setting, pending, pristine] = useUnit([
			model.$modalData,
			model.$pending,
			model.dynamicFormModel.$pristine,
		]);

		const placeholders: Record<string, string> = {
			array: '12.2',
			number: '250.00',
			string: 'String',
			select: `${RTP_MEDIAN_VALUE}`,
		};

		return (
			<ModalForm
				ref={ref}
				title={t('edit-game-setting.title')}
				submitButtonProps={{
					children: t('edit-game-setting.save'),
					loading: pending,
					disabled: pristine,
				}}
				cancelButtonProps={{
					children: t('edit-game-setting.cancel'),
				}}
				{...props}
			>
				<DynamicForm
					id='edit-game-setting-form'
					renderProps={(field) => ({
						rightSection: field.key === 'rtp' ? '%' : setting?.currency,
						label: t(`game-setting.${field.key}`),
						placeholder: placeholders[field.type],
						error:
							field.errors.length > 0 &&
							t(`errors:validation.${field.errors[0]}`, {
								fieldName: t(`game-setting.${field.key}`),
							}),
					})}
					component={Stack}
					gap={12}
				/>
			</ModalForm>
		);
	});

	const EditGameSettingModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-game-setting-action-icon',
			variant: 'ghost-secondary',
			disabled: not(viewerModel.$canEditGameSettings),
			children: <PenSquare />,
		},
	});

	return Object.assign(EditGameSettingModal, {
		ActionIcon: EditGameSettingModalActionIcon,
	});
};
